import React, { useCallback, useEffect, useRef, useState } from "react";
import "./InputForm.css";
import { db } from "../utils/firebase";
import { doc, updateDoc, addDoc, collection, query, where, getDocs } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useLocation } from "react-router-dom";
import {
  createFluentizeTaskDraft,
  createTaskTransport,
  fluentizeTaskCanShare,
  formatTaskClock,
  validateFluentizeTask,
} from "./fluentizeTask";

function rowKey(row, index) {
  return row.localKey || row.id || `row-${index}`;
}

const InputRow = ({
  index,
  data,
  videoDuration,
  anchorRow,
  onSave,
  onShare,
  onShareDirty,
  shareAnchor,
  sharePending,
  playerRef,
  onPracticeStarted,
}) => {
  const [localData, setLocalData] = useState(data);
  const [isDirty, setIsDirty] = useState(!!data.draft);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [sharingLocal, setSharingLocal] = useState(false);
  const sharingRef = useRef(false);
  const location = useLocation();
  const isVoicebotPage = location.pathname === "/voicebot";
  const transportRef = useRef(null);
  const persisted = !!data.id && !data.transient && !data.draft;

  useEffect(() => {
    setLocalData(data);
    setIsDirty(!!data.draft);
    setMessage("");
  }, [data]);

  useEffect(() => {
    const transport = createTaskTransport({
      getPlayer: () => playerRef.current,
      onStarted: (taskId, mode) => onPracticeStarted?.(taskId, mode),
    });
    transportRef.current = transport;
    return () => transport.cancel();
  }, [playerRef, onPracticeStarted]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setLocalData((current) => ({ ...current, [name]: value }));
    setIsDirty(true);
    setMessage("");
  };

  const setFromPlayer = (field) => {
    const player = playerRef.current;
    if (!player || typeof player.getCurrentTime !== "function") return;
    let currentTime = 0;
    try {
      currentTime = player.getCurrentTime();
    } catch {
      return;
    }
    setLocalData((current) => ({ ...current, [field]: formatTaskClock(currentTime) }));
    setIsDirty(true);
    setMessage("");
  };

  useEffect(() => {
    const key = data.id || data.localKey;
    onShareDirty?.(key, isDirty);
    return () => onShareDirty?.(key, false);
  }, [data.id, data.localKey, isDirty, onShareDirty]);

  const handleShare = async () => {
    if (sharePending || sharingRef.current) return;
    sharingRef.current = true;
    setSharingLocal(true);
    try {
      const result = await onShare?.(localData);
      if (result?.ok === false && result.error) setMessage(result.error);
      else if (result?.ok) setMessage("");
    } finally {
      sharingRef.current = false;
      setSharingLocal(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await onSave(index, localData);
    setSaving(false);
    if (!result?.ok) {
      setMessage(result?.errors?.[0] || "Task could not be saved. Try again.");
      return;
    }
    setIsDirty(false);
    setMessage("");
  };

  const practice = (mode) => {
    const transport = transportRef.current;
    const result = mode === "repeat"
      ? transport.repeat(localData, videoDuration)
      : transport.play(localData, videoDuration);
    setMessage(result.ok ? "" : (result.errors?.[0] || "This Task cannot be played yet."));
  };

  const guide = (value) => (anchorRow ? value : undefined);

  return (
    <div className="row-container">
      <div className="row-number">
        {data.transient ? "Shared Task" : `Task ${index + 1}`}
      </div>
      <div className="input-group">
        <label htmlFor={`videoId-${index}`}>Video ID</label>
        <input
          id={`videoId-${index}`}
          type="text"
          name="videoId"
          value={localData.videoId || ""}
          readOnly
          className="IF-input"
        />
      </div>
      <div className="input-group">
        <label htmlFor={`startTime-${index}`}>Start Time</label>
        <input
          id={`startTime-${index}`}
          data-guide={guide("studio-fluentize-start-time")}
          type="text"
          name="startTime"
          value={localData.startTime || "00:00:00"}
          onChange={handleChange}
          className="IF-input small-input"
        />
        <button
          type="button"
          data-guide={guide("studio-fluentize-set-start")}
          onClick={() => setFromPlayer("startTime")}
        >
          Set
        </button>
      </div>
      <div className="input-group">
        <label htmlFor={`stopTime-${index}`}>Stop Time</label>
        <input
          id={`stopTime-${index}`}
          data-guide={guide("studio-fluentize-stop-time")}
          type="text"
          name="stopTime"
          value={localData.stopTime || "00:00:00"}
          onChange={handleChange}
          className="IF-input small-input"
        />
        <button
          type="button"
          data-guide={guide("studio-fluentize-set-stop")}
          onClick={() => setFromPlayer("stopTime")}
        >
          Set
        </button>
      </div>
      <div className="input-group">
        <label htmlFor={`text-${index}`}>Notes</label>
        <textarea
          id={`text-${index}`}
          name="text"
          value={localData.text || ""}
          onChange={handleChange}
          className="IF-input text-field"
        />
      </div>
      <div className="input-group">
        <label htmlFor={`repeatCount-${index}`}>Repeat Count</label>
        <select
          id={`repeatCount-${index}`}
          name="repeatCount"
          value={localData.repeatCount || 1}
          onChange={handleChange}
          className="IF-input"
        >
          {[1, 2, 3, 4, 5, 10].map((count) => (
            <option key={count} value={count}>{count}</option>
          ))}
        </select>
      </div>
      <div className="input-group">
        <label htmlFor={`level-${index}`}>Level</label>
        <select
          id={`level-${index}`}
          name="level"
          value={localData.level || "A1"}
          onChange={handleChange}
          className="IF-input"
        >
          {["A1", "A2", "B1", "B2", "C1", "C2", "M1", "M2"].map((level) => (
            <option key={level} value={level}>{level}</option>
          ))}
        </select>
      </div>
      <div className="input-group">
        <label htmlFor={`focus-${index}`}>Task Type</label>
        <select
          id={`focus-${index}`}
          name="focus"
          value={localData.focus || "Begin"}
          onChange={handleChange}
          className="IF-input"
        >
          {["Begin", "Fluency", "Vocabulary", "Grammar", "Talk", "Idiom", "Persuasion", "Culture", "Text", "Logic", "End"].map((focus) => (
            <option key={focus} value={focus}>{focus}</option>
          ))}
        </select>
      </div>
      {message ? <p className="IF-task-message">{message}</p> : null}
      <div className="SharenSave">
        {isVoicebotPage && persisted && !isDirty ? (
          <button
            type="button"
            className="IF-share-button"
            data-guide={shareAnchor ? "studio-fluentize-share" : undefined}
            onClick={handleShare}
            disabled={sharePending || sharingLocal}
          >
            {sharePending || sharingLocal ? "Sharing..." : "Share"}
          </button>
        ) : null}
        {(data.draft || data.transient || isDirty) ? (
          <button
            type="button"
            className="IF-share-button"
            data-guide={guide("studio-fluentize-save-task")}
            onClick={handleSave}
            disabled={saving}
          >
            {persisted ? "Save Changes" : "Save Task"}
          </button>
        ) : null}
      </div>
      <div className="vidControls">
        <button
          type="button"
          className="RepeatCycle"
          data-guide={guide("studio-fluentize-repeat")}
          onClick={() => practice("repeat")}
        >
          Repeat
        </button>
        <button
          type="button"
          className="RepeatCycle"
          data-guide={guide("studio-fluentize-play")}
          onClick={() => practice("play")}
        >
          Play
        </button>
        <button
          type="button"
          className="RepeatCycle"
          data-guide={guide("studio-fluentize-pause")}
          onClick={() => transportRef.current?.pause()}
        >
          Pause
        </button>
        <button
          type="button"
          className="RepeatCycle"
          data-guide={guide("studio-fluentize-stop")}
          onClick={() => transportRef.current?.stop(localData)}
        >
          Stop
        </button>
      </div>
    </div>
  );
};

const InputForm = ({
  videoId,
  playerRef,
  videoDuration = 0,
  userLevel = "A1",
  sharedTask = null,
  guideAnchors = false,
  onShare,
  sharePending = false,
  preferredShareTaskId = "",
  onTaskCreated,
  onTaskPracticeStarted,
  onGuideStateChange,
}) => {
  const [rows, setRows] = useState([]);
  const [loadPhase, setLoadPhase] = useState("idle");
  const [anchorKey, setAnchorKey] = useState("");
  const [draftSerial, setDraftSerial] = useState(0);
  const [dirtyKeys, setDirtyKeys] = useState({});
  const auth = getAuth();
  const user = auth.currentUser;
  const onGuideStateChangeRef = useRef(onGuideStateChange);
  onGuideStateChangeRef.current = onGuideStateChange;

  useEffect(() => {
    let cancelled = false;
    if (!videoId || !user) {
      setRows([]);
      setLoadPhase(videoId ? "error" : "idle");
      return undefined;
    }
    setLoadPhase("loading");
    setRows([]);
    setAnchorKey("");
    const fetchVideoData = async () => {
      try {
        const taskQuery = query(
          collection(db, "users", user.uid, "tasks"),
          where("videoId", "==", videoId)
        );
        const querySnapshot = await getDocs(taskQuery);
        if (cancelled) return;
        const fetched = querySnapshot.docs.map((item) => ({ id: item.id, ...item.data(), draft: false, transient: false }));
        setRows(fetched);
        setLoadPhase("ready");
      } catch {
        if (!cancelled) {
          setRows([]);
          setLoadPhase("error");
        }
      }
    };
    fetchVideoData();
    return () => {
      cancelled = true;
    };
  }, [videoId, user]);

  useEffect(() => {
    if (!sharedTask || sharedTask.videoId !== videoId || loadPhase !== "ready") return undefined;
    setRows((current) => {
      if (current.some((row) => row.transient && row.videoId === sharedTask.videoId)) return current;
      return [{
        ...sharedTask,
        id: undefined,
        localKey: "shared-task",
        transient: true,
        draft: false,
        videoId,
      }, ...current];
    });
    return undefined;
  }, [sharedTask, videoId, loadPhase]);

  const draftVisible = rows.some((row) => row.draft);
  const hasTasks = rows.some((row) => row.id && !row.transient);
  const shareableRows = rows.filter((row) => (
    !dirtyKeys[row.id || row.localKey]
    && fluentizeTaskCanShare(row, { ownerId: user?.uid, videoDuration })
  ));
  const preferredShare = shareableRows.find((row) => row.id && row.id === preferredShareTaskId);
  const shareTargetId = (preferredShare || shareableRows[0])?.id || "";
  const shareEligible = shareableRows.length > 0;
  const onShareDirty = useCallback((key, dirty) => {
    if (!key) return;
    setDirtyKeys((current) => {
      if (!!current[key] === !!dirty) return current;
      return { ...current, [key]: !!dirty };
    });
  }, []);

  useEffect(() => {
    onGuideStateChangeRef.current?.({
      loadPhase,
      hasTasks,
      draftVisible,
      shareEligible,
    });
  }, [loadPhase, hasTasks, draftVisible, shareEligible]);

  const createDraft = () => {
    const localKey = `draft-${draftSerial + 1}`;
    setDraftSerial((current) => current + 1);
    setAnchorKey(localKey);
    setRows((current) => [...current, { ...createFluentizeTaskDraft({ videoId, userLevel }), localKey }]);
  };

  const saveRow = async (index, data) => {
    const check = validateFluentizeTask({ ...data, videoId: data.videoId || videoId }, videoDuration);
    if (!check.ok) return { ok: false, errors: check.errors };
    if (!user) return { ok: false, errors: ["Sign in to save this Task."] };
    const payload = {
      videoId: data.videoId || videoId,
      startTime: data.startTime,
      stopTime: data.stopTime,
      text: data.text || "",
      repeatCount: Number(data.repeatCount),
      level: data.level,
      focus: data.focus,
      userId: user.uid,
    };
    try {
      let saved = { ...payload, draft: false, transient: false, localKey: data.localKey };
      const isNew = !data.id || data.transient || data.draft;
      if (!isNew) {
        await updateDoc(doc(db, "users", user.uid, "tasks", data.id), payload);
        saved.id = data.id;
      } else {
        const ref = await addDoc(collection(db, "users", user.uid, "tasks"), payload);
        saved.id = ref.id;
        saved.localKey = ref.id;
        setAnchorKey(ref.id);
        onTaskCreated?.(ref.id);
      }
      setRows((current) => current.map((row, rowIndex) => (rowIndex === index ? saved : row)));
      return { ok: true, row: saved };
    } catch {
      return { ok: false, errors: ["Task could not be saved. Try again."] };
    }
  };

  return (
    <div className="scrollable-container">
      <p>Each Task is a clip from the selected video.</p>
      {loadPhase === "loading" ? <p>Loading Tasks…</p> : null}
      {loadPhase === "error" ? <p>Tasks could not be loaded. Try again.</p> : null}
      {loadPhase === "ready" && !hasTasks && !draftVisible ? (
        <p>No Tasks yet for this video. Create a Task to choose a practice segment.</p>
      ) : null}
      {loadPhase === "ready" ? (
        <button
          type="button"
          className="IF-share-button"
          data-guide={guideAnchors ? "studio-fluentize-create-task" : undefined}
          onClick={createDraft}
        >
          Create Task
        </button>
      ) : null}
      {rows.map((row, index) => (
        <InputRow
          key={rowKey(row, index)}
          index={index}
          data={row}
          videoDuration={videoDuration}
          anchorRow={guideAnchors && (row.localKey === anchorKey || row.id === anchorKey)}
          shareAnchor={guideAnchors && !!shareTargetId && row.id === shareTargetId}
          sharePending={sharePending}
          onSave={saveRow}
          onShare={onShare}
          onShareDirty={onShareDirty}
          playerRef={playerRef}
          onPracticeStarted={onTaskPracticeStarted}
        />
      ))}
    </div>
  );
};

export default InputForm;
